import { use, useState, useEffect, createContext, type ReactNode } from 'react';
import { StoredIsColorThemeDark } from '@/shared/utils/Stored';

type ColorThemeContextValue = {
  isDark: boolean;
  switchColorTheme: () => void;
  setForceLight: (value: boolean) => void;
};

const ColorThemeContext = createContext<ColorThemeContextValue | null>(null);

export function useColorTheme() {
  const ctx = use(ColorThemeContext);
  if (!ctx)
    throw new Error('useColorTheme must be used within ColorThemeProvider');
  return ctx;
}

/**
 * Portaled theme-aware components (Modal) read the document-level theme, so
 * a route that must always render light (the landing) forces it globally.
 * One caller at a time — the flag is a boolean, not a counter.
 */
export function useForceLightTheme() {
  const { setForceLight } = useColorTheme();
  useEffect(() => {
    setForceLight(true);
    return () => setForceLight(false);
  }, [setForceLight]);
}

export default function ColorThemeProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [isColorThemeDark, setIsColorThemeDark] = useState(
    StoredIsColorThemeDark.get(),
  );
  const [forceLight, setForceLight] = useState(false);

  const isDark = isColorThemeDark && !forceLight;

  const value: ColorThemeContextValue = {
    switchColorTheme: () => {
      StoredIsColorThemeDark.set(!isColorThemeDark);
      setIsColorThemeDark(!isColorThemeDark);
    },
    isDark,
    setForceLight,
  };

  useEffect(() => {
    if (isDark) {
      document.documentElement.setAttribute('data-theme', 'dark');
    } else {
      document.documentElement.removeAttribute('data-theme');
    }
  }, [isDark]);

  return <ColorThemeContext value={value}>{children}</ColorThemeContext>;
}
